import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Events', path: '/events' },
  ];

  if (isAuthenticated) {
    navItems.push({ label: 'My RSVPs', path: '/my-rsvps' });
  }

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleDashboardClick = () => {
    if (user?.role === 'super_admin' || user?.role === 'event_admin') {
      navigate('/admin/dashboard');
    } else if (user?.role === 'usher') {
      navigate('/usher/dashboard');
    }
    setMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, sm: 64 } }}>
            {/* Logo */}
            <Box
              display="flex"
              alignItems="center"
              sx={{ cursor: 'pointer', flexGrow: { xs: 1, md: 0 } }}
              onClick={() => handleNavigate('/')}
            >
              <EventIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
              <Typography
                variant="h6"
                component="div"
                fontWeight={700}
                color="primary"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                9DesignEvents
              </Typography>
              <Typography
                variant="h6"
                component="div"
                fontWeight={700}
                color="primary"
                sx={{ display: { xs: 'block', sm: 'none' } }}
              >
                Events
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                <Box sx={{ flexGrow: 1, display: 'flex', ml: 4 }}>
                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        mx: 0.5,
                        color: isActivePath(item.path) ? 'primary.main' : 'text.primary',
                        fontWeight: isActivePath(item.path) ? 600 : 400,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: isActivePath(item.path) ? '80%' : 0,
                          height: 2,
                          bgcolor: 'primary.main',
                          transition: theme.custom.transitions.normal,
                        },
                        '&:hover::after': {
                          width: '80%',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>

                <Box display="flex" gap={1}>
                  {isAuthenticated ? (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleDashboardClick}
                        size="medium"
                      >
                        Dashboard
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => handleNavigate('/login')}
                      size="medium"
                    >
                      Sign In
                    </Button>
                  )}
                </Box>
              </>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ ml: 'auto' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '280px',
            maxWidth: '80vw',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700} color="primary">
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  selected={isActivePath(item.path)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box mt={2} display="flex" flexDirection="column" gap={1}>
            {isAuthenticated ? (
              <Button
                variant="contained"
                fullWidth
                onClick={handleDashboardClick}
              >
                Dashboard
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleNavigate('/login')}
              >
                Sign In
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default PublicHeader;
